import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
// import TripRecommendations from '../components/TripRecommendations';

// Helper to send the recommendations email via EmailJS
function sendRecommendationEmail(email, linkUrl) {
  return emailjs.send(
    'service_q2kj03o', // Updated Service ID
    'template_iyxl3oq', // Template ID
    {
      to_email: email, // must match variable in your template
      link_url: linkUrl // must match variable in your template
    },
    'oasHprpcr4slZnQpj' // Public Key
  );
}

// Helper to validate email format
function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const TripPlanner = () => {
  const [step, setStep] = useState(1);
  // const [showRecommendations, setShowRecommendations] = useState(false);
  const [answers, setAnswers] = useState({
    tripDuration: '',
    groupType: '',
    interests: [],
    email: ''
  });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const questions = [
    {
      id: 1,
      question: "¿Cuánto tiempo quieres viajar?",
      type: "select",
      options: [
        { value: "weekend", label: "Fin de semana (2-3 días)" },
        { value: "week", label: "Una semana (4-7 días)" }
      ]
    },
    {
      id: 2,
      question: "¿Con quién viajas?",
      type: "select",
      options: [
        { value: "parejas", label: "En pareja" },
        { value: "familias", label: "Con familia" },
        { value: "grupos", label: "Con amigos" },
        { value: "solo", label: "Solo/a" }
      ]
    },
    {
      id: 3,
      question: "¿Cuáles son tus intereses principales? (Selecciona todos los que apliquen)",
      type: "multiselect",
      options: [
        { value: "beach", label: "Playa y Costa" },
        { value: "mountains", label: "Montaña y Senderismo" },
        { value: "culture", label: "Cultura e Historia" },
        { value: "food", label: "Gastronomía y Vino" },
        { value: "nature", label: "Naturaleza y Vida Silvestre" }
      ]
    },
    {
      id: 4,
      question: "Introduce tu email para recibir tu plan de viaje personalizado",
      type: "email"
    }
  ];

  const handleAnswer = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [questions[step - 1].type === 'multiselect' ? 'interests' : Object.keys(prev)[step - 1]]: answer
    }));
  };

  // Validate the current step before proceeding
  const validateStep = () => {
    const currentQuestion = questions[step - 1];
    if (currentQuestion.type === 'select') {
      const key = Object.keys(answers)[step - 1];
      if (!answers[key]) {
        return 'Por favor, selecciona una opción.';
      }
    } else if (currentQuestion.type === 'multiselect') {
      if (!answers.interests || answers.interests.length === 0) {
        return 'Por favor, selecciona al menos un interés.';
      }
    } else if (currentQuestion.type === 'email') {
      if (!answers.email) {
        return 'Por favor, introduce tu email.';
      }
      if (!isValidEmail(answers.email)) {
        return 'Por favor, introduce un email válido.';
      }
    }
    return '';
  };

  const handleNext = () => {
    const validationError = validateStep();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    if (step < questions.length) {
      setStep(step + 1);
    } else {
      // Build the recommendations link
      const params = new URLSearchParams({
        duration: answers.tripDuration,
        groupType: answers.groupType,
        interests: answers.interests.join(',')
      }).toString();
      const linkUrl = `${window.location.origin}/resources/trip-planner/recommendations?${params}`;

      // Only send email if user provided one
      if (answers.email) {
        sendRecommendationEmail(answers.email, linkUrl)
          .then(() => {
            alert('¡Te hemos enviado un email con tu ruta recomendada!');
            navigate(`/resources/trip-planner/recommendations?${params}`);
          })
          .catch(() => {
            alert('Hubo un problema enviando el email. Puedes ver tu ruta aquí igualmente.');
            navigate(`/resources/trip-planner/recommendations?${params}`);
          });
      } else {
        navigate(`/resources/trip-planner/recommendations?${params}`);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const renderQuestion = () => {
    const currentQuestion = questions[step - 1];

    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden md:max-w-2xl p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {currentQuestion.question}
        </h2>

        {error && (
          <div className="mb-4 text-red-600 text-sm font-medium">{error}</div>
        )}

        {currentQuestion.type === 'select' && (
          <div className="space-y-2">
            {currentQuestion.options.map(option => (
              <button
                key={option.value}
                onClick={() => handleAnswer(option.value)}
                className={`w-full text-left px-4 py-3 rounded-xl ${
                  answers[Object.keys(answers)[step - 1]] === option.value
                    ? 'bg-brand/10 border-brand'
                    : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                } border-2 transition-all duration-300`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {currentQuestion.type === 'multiselect' && (
          <div className="space-y-2">
            {currentQuestion.options.map(option => (
              <button
                key={option.value}
                onClick={() => {
                  const newInterests = answers.interests.includes(option.value)
                    ? answers.interests.filter(i => i !== option.value)
                    : [...answers.interests, option.value];
                  handleAnswer(newInterests);
                }}
                className={`w-full text-left px-4 py-3 rounded-xl ${
                  answers.interests.includes(option.value)
                    ? 'bg-brand/10 border-brand'
                    : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                } border-2 transition-all duration-300`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {currentQuestion.type === 'email' && (
          <input
            type="email"
            value={answers.email}
            onChange={(e) => handleAnswer(e.target.value)}
            placeholder="Introduce tu email"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-brand transition-all duration-300"
          />
        )}

        <div className="mt-6 flex justify-between">
          {step > 1 && (
            <button
              onClick={handleBack}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-300"
            >
              Atrás
            </button>
          )}
          <button
            onClick={handleNext}
            className="ml-auto px-6 py-3 bg-brand text-white rounded-xl hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 transition-all duration-300"
          >
            {step === questions.length ? 'Obtener Mi Plan' : 'Siguiente'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Planificador de Rutas - Furgos Rio</title>
        <meta name="description" content="Planifica tu aventura perfecta en camper desde Vigo, Galicia" />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
            Planificador de Rutas
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Planifica tu aventura perfecta en camper desde Vigo
          </p>
        </div>

        <div className="mt-8">
          {renderQuestion()}
        </div>
      </div>
    </div>
  );
};

export default TripPlanner; 